import { withTerminalBlocksForExport } from "../withTerminalBlocksForExport";
import { packTerminalBridges } from "../terminalBridgePacking";
import { pdfHeaderTag } from "../pdfTagName";
import type { State } from "../../State";
import type Device from "../../models/Device";
import type DeviceTemplate from "../../models/DeviceTemplate";
import type Terminal from "../../models/Terminal";
import type { PdfTerminalSpacing } from "../PdfExportConfig";
import { label, sorted, number, type TechnicalReport, type ReportSection, type ReportTable } from "./types";
import { channelTypeInfo } from "../../models/ChannelType";

/** Reads product dimensions and bus consumption consistently across all equipment lists. */
export function deviceMetrics(template?: DeviceTemplate) {
  const info = template?.knxProductData?.productInfo;
  return { te: Math.max(0, Math.ceil(number(info?.WidthInMillimeter) / 9) / 2),
    bus: info?.SpecialUsage === "PowerSupply" ? 0 : Math.max(0, number(info?.BusCurrent)) };
}

/** Groups actual device instances by template identity, independent of template hierarchy depth. */
function usedTemplates(devices: Device[]) {
  const result = new Map<string, { template: DeviceTemplate; devices: Device[] }>();
  for (const device of sorted(devices)) {
    const template = device.template;
    if (!template) continue;
    const entry = result.get(template.id) ?? { template, devices: [] };
    entry.devices.push(device); result.set(template.id, entry);
  }
  return [...result.values()].sort((a, b) => label(a.template).localeCompare(label(b.template), "de", { numeric: true }));
}

/** Describes every used template and its concrete device instances. */
export function templateReport(app: State): TechnicalReport {
  const sections: ReportSection[] = [];
  for (const { template, devices } of usedTemplates(app.allDevices())) {
    const metrics = deviceMetrics(template);
    sections.push({ title: label(template), hierarchy: [label(template), ""], notes: [label(template.parent)], tables: [
      { columns: ["Anzahl", "Gesamt TE", "Gesamt Busstrom"], rows: [[String(devices.length), String(metrics.te * devices.length), `${metrics.bus * devices.length} mA`]] },
      { columns: ["Gerät", "Name", "Ort", "Physikalische Adresse"], rows: devices.map(device => [pdfHeaderTag(device), device.name, label(device.parent), device.fullPhysicalAddress]) },
    ] });
  }
  const missing = sorted(app.allDevices()).filter(device => !device.template);
  if (missing.length) sections.push({ title: "Ohne Produktauswahl", hierarchy: ["Ohne Produktauswahl", ""], tables: [{ columns: ["Gerät", "Name", "Ort"], rows: missing.map(device => [pdfHeaderTag(device), device.name, label(device.parent)]) }] });
  return { sections };
}

/** Parses stored decimal prices; missing/invalid prices remain unknown, including in subtotals. */
export function parseProductPrice(raw: unknown): number | undefined {
  if (raw === undefined || raw === null) return undefined;
  let value = String(raw).trim().replace(/EUR|€/gi, "").replace(/\s/g, "");
  if (!value) return undefined;
  if (value.includes(",")) value = value.replace(/\./g, "").replace(",", ".");
  if (!/^\d+(\.\d+)?$/.test(value)) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/** Uses the same article-specific retailer snapshot as the product UI. */
export function storedProductPrice(template: DeviceTemplate): number | undefined {
  const info = template.knxProductData?.productInfo;
  const articleNo = template.orderInformation?.articleNo || info?.OrderNumber;
  return parseProductPrice(articleNo ? info?.RetailerData?.[articleNo]?.price : undefined);
}

function priceSection(title: string, devices: Device[]): ReportSection {
  devices = devices.filter(device => !channelTypeInfo(device.channelType).isSupply);
  const rows: string[][] = [];
  let sum = 0, missing = 0;
  const money = (value: number) => value.toLocaleString("de-DE", { style: "currency", currency: "EUR" });
  for (const { template, devices: usages } of usedTemplates(devices)) {
    const price = storedProductPrice(template);
    const count = usages.length;
    if (price === undefined) missing += count; else sum += price * count;
    const info = template.knxProductData?.productInfo;
    const articleNo = template.orderInformation?.articleNo || info?.OrderNumber;
    const availability = articleNo ? info?.RetailerData?.[articleNo]?.availability : undefined;
    rows.push([template.name, String(count), price === undefined ? "Fehlt" : money(price), price === undefined ? "Unbekannt" : money(price * count), availability || "Unbekannt"]);
  }
  const withoutTemplate = sorted(devices).filter(device => !device.template).length;
  missing += withoutTemplate;
  if (withoutTemplate) rows.push(["Geräte ohne Produktauswahl", String(withoutTemplate), "Fehlt", "Unbekannt", "Unbekannt"]);
  rows.push([missing ? "Summe bekannter Preise (unvollständig)" : "Summe", "", "", money(sum), ""]);
  return { title, hierarchy: ["", ""], tables: [{ columns: ["Produkt", "Anzahl", "Einzelpreis", "Gesamtpreis", "Verfügbarkeit"], rows }],
    notes: missing ? [`Für ${missing} Geräte fehlt ein Preis.`] : [] };
}

/** Provides the total and optional cabinet-specific price breakdowns without double-counting totals. */
export function priceReport(app: State, perCabinet: boolean): TechnicalReport {
  const sections = [priceSection("Projekt", app.allDevices())];
  if (perCabinet) for (const cabinet of sorted(app.cabinets.asArray())) {
    sections.push(priceSection(label(cabinet), cabinet.actors.asArray()));
  }
  sections[0].notes = [...sections[0].notes ?? [], "Preise und Verfügbarkeit aus den gemeinsam mit der Produktansicht gespeicherten Händlerdaten. Die Summe umfasst nur die aufgeführten Geräte."];
  return { sections };
}

/** Packs device widths into 12-TE rows using the same non-splitting assumption as the old list. */
function requiredRows(widths: number[]): number {
  let rows = 0, remaining = 0;
  for (const width of widths) {
    if (width <= 0) continue;
    if (width > 12) { rows += Math.ceil(width / 12); remaining = 0; }
    else if (width <= remaining) remaining -= width;
    else { rows++; remaining = 12 - width; }
  }
  return rows;
}

/** Lists cabinet components, including terminal and protective-device capacity estimates. */
export function cabinetPartsReport(app: State): TechnicalReport {
  const sections: ReportSection[] = [];
  for (const cabinet of sorted(app.cabinets.asArray())) {
    const rows: string[][] = [], widths: number[] = [];
    const tables: ReportSection["tables"] = [];
    const columns = ["Tag", "Name", "Produkt / Bauteil", "TE", "Busstrom"];
    let totalTE = 0, bus = 0;
    let groupTE = 0;
    const finishGroup = (title: string, groupBus?: number) => {
      if (!rows.length) return;
      const count = requiredRows(widths);
      rows.push([`Summe ${title}`, "", `${count} Reihen à 12 TE (${count * 12} TE)`, String(groupTE), groupBus === undefined ? "" : `${groupBus} mA`]);
      tables.push({ title, layoutKey: "cabinet-parts", columns, rows: rows.splice(0) });
      groupTE = 0;
      widths.length = 0;
    };
    for (const actor of sorted(cabinet.actors.asArray())) {
      if (channelTypeInfo(actor.channelType).isSupply) continue;
      const metric = deviceMetrics(actor.template);
      totalTE += metric.te; groupTE += metric.te; bus += metric.bus; widths.push(metric.te);
      rows.push([pdfHeaderTag(actor), actor.name, actor.template?.name || "Ohne Produktauswahl", String(metric.te), `${metric.bus} mA`]);
    }
    finishGroup("Aktorik", bus);
    let totalLnpe = 0, totalLl = 0;
    for (const strip of sorted(cabinet.terminalStrips.asArray())) {
      let lnpe = 0, ll = 0;
      for (const block of strip.getTerminalBlocks()) {
        const wires = block.cableType?.numberOfWires || 0;
        if (wires >= 3) { lnpe++; ll += Math.ceil(Math.max(0, wires - 3) / 2); }
      }
      const te = Math.ceil((lnpe + ll) / 2);
      totalLnpe += lnpe; totalLl += ll;
      totalTE += te; groupTE += te; widths.push(te);
      rows.push([pdfHeaderTag(strip), strip.name, `${lnpe} L/N/PE · ${ll} L/L Klemmen`, String(te), ""]);
    }
    finishGroup("Klemmen");
    for (const elcb of sorted(cabinet.elcbs.asArray())) {
      totalTE += 4; groupTE += 4;
      rows.push([pdfHeaderTag(elcb), elcb.name, "FI", "4", ""]);
      let groupWidth = 4;
      for (const fuse of sorted(elcb.fuses.asArray())) {
        const te = fuse.getSize(); totalTE += te; groupTE += te; groupWidth += te;
        rows.push([pdfHeaderTag(fuse), fuse.name, `${fuse.consumers.length} Verbraucher`, String(te), ""]);
      }
      widths.push(groupWidth);
    }
    for (const fuse of sorted(cabinet.fi_ls_s.asArray())) {
      totalTE += 2; groupTE += 2; widths.push(2);
      rows.push([pdfHeaderTag(fuse), fuse.name, "FI/LS", "2", ""]);
    }
    finishGroup("Sicherungen");
    tables.push({ title: "Gesamt", layoutKey: "cabinet-parts", columns, rows: [
      ["Summe", "", `${totalLnpe} L/N/PE · ${totalLl} L/L Klemmen`, String(totalTE), ""],
    ] });
    sections.push({ title: label(cabinet), hierarchy: [label(cabinet), ""], tables,
      notes: ["Platzbedarf als Schätzung: zwei Klemmen je TE, FI 4 TE, FI/LS 2 TE. Geräte ohne Produktmaße sind im Platzbedarf nicht berücksichtigt. Vorgelagerte Bauteile und Montagezubehör sind nicht enthalten."] });
  }
  return { sections };
}

/** Resolves the cable's wire label instead of exposing an HTML colour code in the list. */
function terminalWire(terminal: Terminal): string {
  const block = terminal.getTerminalBlock();
  const consumer = block?.mainTerminal.consumer;
  const index = consumer ? consumer.fullWireIndex(terminal.terminalIndex) : 2;
  return block?.cableType?.wireDefinitions?.[index]?.longName || "";
}

/** Reads the same computed terminal information as the navigator without embedding its UI. */
export function terminalReport(app: State, showUnusedWires = false, spacing: PdfTerminalSpacing = "normal"): TechnicalReport {
  return withTerminalBlocksForExport(app, showUnusedWires, () => readTerminalReport(app, spacing));
}

/** Counts cables once per terminal block; the common type is followed by explicit exceptions. */
function terminalCableNotes(terminals: Terminal[]): string[] {
  const blocks = new Map<NonNullable<ReturnType<Terminal["getTerminalBlock"]>>, Terminal[]>();
  for (const terminal of terminals) {
    const block = terminal.getTerminalBlock();
    if (block) blocks.set(block, [...blocks.get(block) ?? [], terminal]);
  }
  const groups = new Map<number, Map<string, string[]>>();
  for (const [block, terminals] of blocks) {
    const cable = block.cableType;
    if (!cable || !cable.numberOfWires) continue;
    const types = groups.get(cable.numberOfWires) ?? new Map<string, string[]>();
    const name = cable.name || cable.fullTag;
    const references = types.get(name) ?? [];
    const tags = [...new Set(terminals.map(terminal => terminal.tag))].join(", ");
    const consumerTag = block.mainTerminal.consumer?.fullTag;
    references.push(`Klemmen ${tags}${consumerTag ? ` · ${consumerTag}` : ""}`);
    types.set(name, references); groups.set(cable.numberOfWires, types);
  }
  if (!groups.size) return [];
  const notes = ["Es werden folgende Kabel verwendet"];
  for (const [wires, types] of [...groups].sort(([a], [b]) => a - b)) {
    notes.push(`Für ${wires}-fach Kabel`);
    const sortedTypes = [...types].sort(([a, usagesA], [b, usagesB]) => usagesB.length - usagesA.length || a.localeCompare(b, "de", { numeric: true }));
    const [mainName] = sortedTypes[0];
    if (sortedTypes.length === 1) notes.push(`  • alle ${mainName}`);
    else {
      const exceptions = sortedTypes.slice(1).flatMap(([, references]) => references);
      notes.push(`  • ${mainName}: alle außer ${exceptions.join("; ")}`);
      for (const [name, references] of sortedTypes.slice(1)) notes.push(`  • ${name}: ${references.join("; ")}`);
    }
  }
  return notes;
}

function readTerminalReport(app: State, spacing: PdfTerminalSpacing): TechnicalReport {
  const sections: ReportSection[] = [];
  for (const cabinet of sorted(app.cabinets.asArray())) for (const strip of sorted(cabinet.terminalStrips.asArray())) {
    strip.calcListInfos();
    const terminals = sorted(strip.terminals.asArray());
    const bridgeRows = terminals.map(terminal => {
      const copy = (infos: NonNullable<Terminal["elcbInfos"]> = []) => infos.map(info => ({ id: info.obj.id, tag: info.obj.tag,
        incoming: info.bridgeIn, outgoing: info.bridgeOut, dot: info.dot }));
      return { neutral: copy(terminal.elcbInfos), live: copy(terminal.fuseInfos) };
    });
    const blocks = packTerminalBridges({ rows: bridgeRows });
    const laneCount = blocks.reduce((count, block) => Math.max(count, block.lane + 1), 0);
    const bridgeColumns: NonNullable<ReportTable["bridgeColumns"]> = Array.from({ length: laneCount }, (_, lane) => ({
      column: 3 + lane, rows: bridgeRows.map((row, index) => {
        const block = blocks.find(item => item.lane === lane && index >= item.start && index <= item.end);
        const info = block && row[block.kind].find(item => `${block.kind}:${item.id}` === block.key);
        return block && info ? { ...info, kind: block.kind, start: index === block.start } : undefined;
      }),
    }));
    const bulletNoteCells: { row: number; column: number }[] = [];
    const rowGroupStarts: number[] = [];
    let previousBlock: ReturnType<Terminal["getTerminalBlock"]>;
    const rows = terminals.map((terminal, row) => {
      const connection = terminal.getWireAndConsumer?.();
      const consumer = connection?.consumer;
      const block = terminal.getTerminalBlock();
      const startsBlock = row === 0 || block !== previousBlock;
      if (startsBlock) rowGroupStarts.push(row);
      previousBlock = block;
      const directlyConnected = block?.mainTerminal.consumer;
      const hasViaConsumers = !!directlyConnected?.getAllViaConsumers?.().length;
      const via = !!consumer?.getTerminalViaConsumer() || hasViaConsumers;
      const self = !!consumer && consumer === directlyConnected && hasViaConsumers;
      const functionIndex = connection?.consumerWireIndexWithoutPEandN ?? -1;
      const wireFunction = !block?.isCollapsed && functionIndex >= 0
        ? consumer?.wireRequirement?.functionOfWires?.[functionIndex] : undefined;
      const destination = self ? "selbst" : label(consumer);
      const destinationWithFunction = wireFunction && wireFunction !== "L" ? `${destination} · ${wireFunction}` : destination;
      if (self) bulletNoteCells.push({ row, column: 5 + bridgeColumns.length });
      return [startsBlock ? pdfHeaderTag(directlyConnected?.fuse) : "", pdfHeaderTag(consumer?.getConnectedChannel()), terminal.tag,
        ...bridgeColumns.map(() => ""),
        terminalWire(terminal), via && startsBlock ? label(directlyConnected) : "", destinationWithFunction];
    });
    sections.push({ title: `${label(cabinet)} · ${label(strip)}`, notes: terminalCableNotes(terminals), tables: [{
      columns: ["Sicherung", "Aktor", "Klemme", ...bridgeColumns.map(() => ""),
        "Ader", "Unterverteiler", "Verbraucher"],
      rows, bulletNoteCells, rowGroupStarts, groupedColumns: [0], groupBorderColor: "#000000", bridgeColumns,
      headerSpans: [{ column: 2, count: 1 + bridgeColumns.length }],
      minimumRowHeight: spacing === "compact" ? 28 * 0.6 - 2 * 72 / 25.4 : (spacing === "wide" ? 44 : 36) * 0.6,
    }] });
  }
  return { sections, landscape: false };
}
